import { defineMessages } from './define'

/* Lab stock: the items the clinic keeps count of, stock coming in and being
 * used, what each test uses, the "mark done" sheet, and the stock reports. */

export default defineMessages({
  en: {
    'stock.outOfStock': 'Out of stock',
    'stock.lowAt': 'Low (≤ {n})',
    'stock.allow': 'Allow stock updates',
    'stock.revoke': 'Stop stock updates',
    'stock.price': 'Price per unit',
    'stock.title': 'Stock',
    'stock.items': 'Items',
    'stock.value': 'Stock value',
    'stock.low': 'Low',
    'stock.out': 'Out',
    'stock.search': 'Search items',
    'stock.onHand': 'On hand',
    'stock.perUnit': '{price} per {unit}',
    'stock.worth': 'worth {value}',
    'stock.alertAt': 'Alert at {n}',
    'stock.noItems': 'No stock items yet',
    'stock.noItemsBody': 'Add an item, or import your list of items with their prices. Quantities can be counted in later.',
    'stock.noMatch': 'Nothing matches',
    'stock.noMatchBody': 'Try another word, or clear the filter.',
    'stock.addItem': 'Add item',
    'stock.importList': 'Import list',
    'stock.newItem': 'New stock item',
    'stock.editItem': 'Edit item',
    'stock.name': 'Item name',
    'stock.unit': 'Unit',
    'stock.unitPh': 'pcs, ml, box…',
    'stock.category': 'Category',
    'stock.unitCost': 'Buying price per unit (₹)',
    'stock.reorderLevel': 'Alert when stock falls to',
    'stock.reorderHint': 'Leave 0 for no alert.',
    'stock.opening': 'Stock on hand now',
    'stock.openingHint': 'Optional. You can count it in later.',
    'stock.saveItem': 'Save item',
    'stock.itemAdded': '{name} added.',
    'stock.itemSaved': 'Saved.',
    'stock.couldNotLoad': 'Could not load stock.',
    'stock.couldNotSave': 'Could not save.',
    'stock.importTitle': 'Import your item list',
    'stock.importNote': 'One item per line: name, unit, price, alert level, quantity. Quantity is optional. An item already in the list only has its price and unit updated, never its quantity.',
    'stock.importPh': 'EDTA tube, pcs, 6, 20\nGloves, pair, 4, 10, 100',
    'stock.pickFile': 'Choose a CSV file',
    'stock.importGo': 'Import {n} items',
    'stock.importDone': '{created} added, {updated} updated.',
    'stock.importNone': 'No items found in that text.',
    'stock.history': 'History',
    'stock.noHistory': 'Nothing recorded yet.',
    'stock.usedIn': 'Used in these tests',
    'stock.stockIn': 'Stock in',
    'stock.count': 'Count',
    'stock.use': 'Used',
    'stock.quantity': 'Quantity',
    'stock.supplier': 'Supplier',
    'stock.note': 'Note',
    'stock.noteBill': 'Bill number or remark',
    'stock.newPrice': 'Buying price per unit (₹)',
    'stock.newPriceHint': 'Changes the item’s current price.',
    'stock.counted': 'Counted on the shelf',
    'stock.countHint': 'Type what is really there. The difference is recorded.',
    'stock.useHint': 'For breakage, expiry or a spoilt sample. Lab tests record their own use.',
    'stock.recorded': 'Recorded.',
    'stock.countSame': 'The count matches, nothing to change.',
    'stock.countDiff': 'Corrected by {n}.',
    'stock.kind.in': 'Stock in',
    'stock.kind.use': 'Used',
    'stock.kind.adjust': 'Count',
    'stock.by': 'by {name}',
    'stock.forTest': 'for {test}',
    'stock.retire': 'Retire item',
    'stock.restore': 'Restore item',
    'stock.retiredNote': 'Retired: hidden from every picker, history kept.',
    'stock.viewOnly': 'You can view stock. Ask an admin to let you update it.',
    'stock.reports': 'Stock reports',
    'stock.rpStock': 'Stock',
    'stock.rpUsage': 'Usage',
    'stock.rpTests': 'By test',
    'stock.totalValue': 'Total stock value',
    'stock.byCategory': 'By category',
    'stock.toReorder': 'To reorder',
    'stock.nothingLow': 'Nothing is low right now.',
    'stock.usedCost': 'Used (cost)',
    'stock.testsDone': 'Tests done',
    'stock.testIncome': 'Test income',
    'stock.usedByItem': 'Used, by item',
    'stock.noUsage': 'No stock was used in this period.',
    'stock.received': 'received {n}',
    'stock.used': 'used {n}',
    'stock.inTests': 'in tests {n}',
    'stock.doneOfBooked': '{done} done of {booked}',
    'stock.stockCost': 'stock {cost}',
    'stock.margin': 'left {margin}',
    'stock.noTests': 'No lab tests in this period.',
    'stock.rpNote': 'Usage is valued at the buying price on the day it was used.',
    'stock.itemsUsed': 'Items used',
    'stock.recipeTitle': 'What {name} uses',
    'stock.recipeNote': 'When this test is marked done these are filled in for you, so the lab only confirms what was used.',
    'stock.recipeNone': 'No items set for this test.',
    'stock.recipeAdd': 'Add an item',
    'stock.recipePick': 'Choose an item',
    'stock.recipeSave': 'Save',
    'stock.recipeSaved': 'Saved for this test.',
    'stock.usesN': 'Uses {n} items',
    'stock.noRecipe': 'No items set',
    'stock.markDone': 'Mark done',
    'stock.doneTitle': 'Mark {test} done',
    'stock.doneNote': 'Confirm what was used. It is taken out of stock now.',
    'stock.doneNoRecipe': 'No items are set for this test. Add what was used, or mark it done without stock.',
    'stock.addOther': 'Add another item',
    'stock.confirmDone': 'Mark done',
    'stock.doneToast': '{test} marked done.',
    'stock.usedInTest': 'Used in this test',
    'stock.short': 'Only {n} {unit} on hand',
    'stock.remove': 'Remove',
    'stock.canUpdate': 'Can update stock',
    'stock.canUpdateHint': 'Lets this person add stock, count it and set what each test uses.',
    'stock.switchedOn': '{name} can now update stock.',
    'stock.switchedOff': '{name} can no longer update stock.',
    'stock.labToday': 'Lab today',
    'stock.labToDo': 'To do',
    'stock.labDone': 'Done',
    'stock.runningLow': 'Running low',
    'stock.openStock': 'Open stock',
    'stock.labTests': 'Lab tests',
    'stock.allGood': 'Every item is above its alert level.',
  },
  hi: {
    'stock.outOfStock': 'स्टॉक ख़त्म',
    'stock.lowAt': 'कम (≤ {n})',
    'stock.allow': 'स्टॉक बदलने दें',
    'stock.revoke': 'स्टॉक बदलना बंद करें',
    'stock.price': 'प्रति इकाई कीमत',
    'stock.title': 'स्टॉक',
    'stock.items': 'आइटम',
    'stock.value': 'स्टॉक मूल्य',
    'stock.low': 'कम',
    'stock.out': 'ख़त्म',
    'stock.search': 'आइटम खोजें',
    'stock.onHand': 'उपलब्ध',
    'stock.perUnit': '{price} प्रति {unit}',
    'stock.worth': 'कीमत {value}',
    'stock.alertAt': '{n} पर अलर्ट',
    'stock.noItems': 'अभी कोई स्टॉक आइटम नहीं',
    'stock.noItemsBody': 'आइटम जोड़ें, या कीमतों के साथ अपनी सूची इम्पोर्ट करें। मात्रा बाद में गिनकर डाली जा सकती है।',
    'stock.noMatch': 'कुछ नहीं मिला',
    'stock.noMatchBody': 'कोई और शब्द आज़माएँ, या फ़िल्टर हटाएँ।',
    'stock.addItem': 'आइटम जोड़ें',
    'stock.importList': 'सूची इम्पोर्ट करें',
    'stock.newItem': 'नया स्टॉक आइटम',
    'stock.editItem': 'आइटम बदलें',
    'stock.name': 'आइटम का नाम',
    'stock.unit': 'इकाई',
    'stock.unitPh': 'पीस, मिली, डिब्बा…',
    'stock.category': 'श्रेणी',
    'stock.unitCost': 'खरीद मूल्य प्रति इकाई (₹)',
    'stock.reorderLevel': 'स्टॉक इतना रह जाए तो अलर्ट',
    'stock.reorderHint': 'अलर्ट न चाहिए तो 0 रखें।',
    'stock.opening': 'अभी उपलब्ध स्टॉक',
    'stock.openingHint': 'वैकल्पिक। बाद में गिनकर डाल सकते हैं।',
    'stock.saveItem': 'आइटम सहेजें',
    'stock.itemAdded': '{name} जोड़ा गया।',
    'stock.itemSaved': 'सहेजा गया।',
    'stock.couldNotLoad': 'स्टॉक लोड नहीं हो सका।',
    'stock.couldNotSave': 'सहेज नहीं सके।',
    'stock.importTitle': 'अपनी आइटम सूची इम्पोर्ट करें',
    'stock.importNote': 'हर लाइन में एक आइटम: नाम, इकाई, कीमत, अलर्ट स्तर, मात्रा। मात्रा वैकल्पिक है। सूची में पहले से मौजूद आइटम की सिर्फ़ कीमत और इकाई बदलती है, मात्रा कभी नहीं।',
    'stock.importPh': 'EDTA ट्यूब, पीस, 6, 20\nदस्ताने, जोड़ी, 4, 10, 100',
    'stock.pickFile': 'CSV फ़ाइल चुनें',
    'stock.importGo': '{n} आइटम इम्पोर्ट करें',
    'stock.importDone': '{created} जोड़े, {updated} बदले।',
    'stock.importNone': 'इस पाठ में कोई आइटम नहीं मिला।',
    'stock.history': 'इतिहास',
    'stock.noHistory': 'अभी कुछ दर्ज नहीं।',
    'stock.usedIn': 'इन जाँचों में उपयोग',
    'stock.stockIn': 'स्टॉक आया',
    'stock.count': 'गिनती',
    'stock.use': 'उपयोग',
    'stock.quantity': 'मात्रा',
    'stock.supplier': 'सप्लायर',
    'stock.note': 'टिप्पणी',
    'stock.noteBill': 'बिल नंबर या टिप्पणी',
    'stock.newPrice': 'खरीद मूल्य प्रति इकाई (₹)',
    'stock.newPriceHint': 'इससे आइटम का मौजूदा मूल्य बदल जाता है।',
    'stock.counted': 'शेल्फ़ पर गिनी गई मात्रा',
    'stock.countHint': 'जो सच में है वही लिखें। अंतर दर्ज हो जाएगा।',
    'stock.useHint': 'टूट-फूट, एक्सपायरी या ख़राब सैंपल के लिए। लैब जाँच अपना उपयोग ख़ुद दर्ज करती है।',
    'stock.recorded': 'दर्ज हो गया।',
    'stock.countSame': 'गिनती मेल खाती है, कुछ बदलना नहीं।',
    'stock.countDiff': '{n} से सुधारा गया।',
    'stock.kind.in': 'स्टॉक आया',
    'stock.kind.use': 'उपयोग',
    'stock.kind.adjust': 'गिनती',
    'stock.by': '{name} द्वारा',
    'stock.forTest': '{test} के लिए',
    'stock.retire': 'आइटम बंद करें',
    'stock.restore': 'आइटम फिर चालू करें',
    'stock.retiredNote': 'बंद: किसी सूची में नहीं दिखता, इतिहास सुरक्षित।',
    'stock.viewOnly': 'आप स्टॉक देख सकते हैं। बदलने के लिए एडमिन से अनुमति लें।',
    'stock.reports': 'स्टॉक रिपोर्ट',
    'stock.rpStock': 'स्टॉक',
    'stock.rpUsage': 'उपयोग',
    'stock.rpTests': 'जाँच अनुसार',
    'stock.totalValue': 'कुल स्टॉक मूल्य',
    'stock.byCategory': 'श्रेणी अनुसार',
    'stock.toReorder': 'दोबारा मँगाएँ',
    'stock.nothingLow': 'अभी कुछ भी कम नहीं है।',
    'stock.usedCost': 'उपयोग (लागत)',
    'stock.testsDone': 'पूरी हुई जाँच',
    'stock.testIncome': 'जाँच से आय',
    'stock.usedByItem': 'आइटम अनुसार उपयोग',
    'stock.noUsage': 'इस अवधि में कोई स्टॉक उपयोग नहीं हुआ।',
    'stock.received': 'आया {n}',
    'stock.used': 'उपयोग {n}',
    'stock.inTests': 'जाँच में {n}',
    'stock.doneOfBooked': '{booked} में से {done} पूरी',
    'stock.stockCost': 'स्टॉक {cost}',
    'stock.margin': 'बचा {margin}',
    'stock.noTests': 'इस अवधि में कोई लैब जाँच नहीं।',
    'stock.rpNote': 'उपयोग का मूल्य उस दिन के खरीद मूल्य से लगाया जाता है।',
    'stock.itemsUsed': 'उपयोग हुए आइटम',
    'stock.recipeTitle': '{name} में क्या लगता है',
    'stock.recipeNote': 'यह जाँच पूरी होने पर ये अपने-आप भर जाते हैं, लैब को बस पुष्टि करनी होती है।',
    'stock.recipeNone': 'इस जाँच के लिए कोई आइटम तय नहीं।',
    'stock.recipeAdd': 'आइटम जोड़ें',
    'stock.recipePick': 'आइटम चुनें',
    'stock.recipeSave': 'सहेजें',
    'stock.recipeSaved': 'इस जाँच के लिए सहेजा गया।',
    'stock.usesN': '{n} आइटम लगते हैं',
    'stock.noRecipe': 'कोई आइटम तय नहीं',
    'stock.markDone': 'पूरा करें',
    'stock.doneTitle': '{test} पूरी करें',
    'stock.doneNote': 'जो उपयोग हुआ उसकी पुष्टि करें। यह अभी स्टॉक से घट जाएगा।',
    'stock.doneNoRecipe': 'इस जाँच के लिए कोई आइटम तय नहीं। जो लगा वह जोड़ें, या बिना स्टॉक के पूरी करें।',
    'stock.addOther': 'और आइटम जोड़ें',
    'stock.confirmDone': 'पूरा करें',
    'stock.doneToast': '{test} पूरी हुई।',
    'stock.usedInTest': 'इस जाँच में उपयोग',
    'stock.short': 'सिर्फ़ {n} {unit} उपलब्ध',
    'stock.remove': 'हटाएँ',
    'stock.canUpdate': 'स्टॉक बदल सकते हैं',
    'stock.canUpdateHint': 'यह व्यक्ति स्टॉक जोड़ सकेगा, गिन सकेगा और हर जाँच का सामान तय कर सकेगा।',
    'stock.switchedOn': '{name} अब स्टॉक बदल सकते हैं।',
    'stock.switchedOff': '{name} अब स्टॉक नहीं बदल सकते।',
    'stock.labToday': 'आज की लैब',
    'stock.labToDo': 'करना बाकी',
    'stock.labDone': 'पूरी',
    'stock.runningLow': 'कम पड़ रहे',
    'stock.openStock': 'स्टॉक खोलें',
    'stock.labTests': 'लैब जाँच',
    'stock.allGood': 'हर आइटम अलर्ट स्तर से ऊपर है।',
  },
  mr: {
    'stock.outOfStock': 'स्टॉक संपला',
    'stock.lowAt': 'कमी (≤ {n})',
    'stock.allow': 'स्टॉक बदलण्याची परवानगी द्या',
    'stock.revoke': 'स्टॉक बदलणे बंद करा',
    'stock.price': 'प्रति एकक किंमत',
    'stock.title': 'स्टॉक',
    'stock.items': 'वस्तू',
    'stock.value': 'स्टॉक किंमत',
    'stock.low': 'कमी',
    'stock.out': 'संपले',
    'stock.search': 'वस्तू शोधा',
    'stock.onHand': 'उपलब्ध',
    'stock.perUnit': '{price} प्रति {unit}',
    'stock.worth': 'किंमत {value}',
    'stock.alertAt': '{n} वर अलर्ट',
    'stock.noItems': 'अजून स्टॉक वस्तू नाहीत',
    'stock.noItemsBody': 'वस्तू जोडा, किंवा किमतींसह तुमची यादी इम्पोर्ट करा. प्रमाण नंतर मोजून भरता येते.',
    'stock.noMatch': 'काहीही सापडले नाही',
    'stock.noMatchBody': 'दुसरा शब्द वापरून पहा, किंवा फिल्टर काढा.',
    'stock.addItem': 'वस्तू जोडा',
    'stock.importList': 'यादी इम्पोर्ट करा',
    'stock.newItem': 'नवीन स्टॉक वस्तू',
    'stock.editItem': 'वस्तू बदला',
    'stock.name': 'वस्तूचे नाव',
    'stock.unit': 'एकक',
    'stock.unitPh': 'नग, मिली, बॉक्स…',
    'stock.category': 'प्रकार',
    'stock.unitCost': 'खरेदी किंमत प्रति एकक (₹)',
    'stock.reorderLevel': 'स्टॉक इतका उरला की अलर्ट',
    'stock.reorderHint': 'अलर्ट नको असल्यास 0 ठेवा.',
    'stock.opening': 'आत्ता उपलब्ध स्टॉक',
    'stock.openingHint': 'ऐच्छिक. नंतर मोजून भरता येईल.',
    'stock.saveItem': 'वस्तू जतन करा',
    'stock.itemAdded': '{name} जोडले.',
    'stock.itemSaved': 'जतन केले.',
    'stock.couldNotLoad': 'स्टॉक लोड झाला नाही.',
    'stock.couldNotSave': 'जतन करता आले नाही.',
    'stock.importTitle': 'तुमची वस्तू यादी इम्पोर्ट करा',
    'stock.importNote': 'प्रत्येक ओळीत एक वस्तू: नाव, एकक, किंमत, अलर्ट पातळी, प्रमाण. प्रमाण ऐच्छिक आहे. यादीत आधीच असलेल्या वस्तूची फक्त किंमत व एकक बदलते, प्रमाण कधीही नाही.',
    'stock.importPh': 'EDTA ट्यूब, नग, 6, 20\nहातमोजे, जोडी, 4, 10, 100',
    'stock.pickFile': 'CSV फाइल निवडा',
    'stock.importGo': '{n} वस्तू इम्पोर्ट करा',
    'stock.importDone': '{created} जोडल्या, {updated} बदलल्या.',
    'stock.importNone': 'त्या मजकुरात वस्तू सापडल्या नाहीत.',
    'stock.history': 'इतिहास',
    'stock.noHistory': 'अजून काही नोंद नाही.',
    'stock.usedIn': 'या तपासण्यांत वापर',
    'stock.stockIn': 'स्टॉक आला',
    'stock.count': 'मोजणी',
    'stock.use': 'वापर',
    'stock.quantity': 'प्रमाण',
    'stock.supplier': 'पुरवठादार',
    'stock.note': 'टीप',
    'stock.noteBill': 'बिल क्रमांक किंवा शेरा',
    'stock.newPrice': 'खरेदी किंमत प्रति एकक (₹)',
    'stock.newPriceHint': 'यामुळे वस्तूची सध्याची किंमत बदलते.',
    'stock.counted': 'शेल्फवर मोजलेले',
    'stock.countHint': 'प्रत्यक्षात जे आहे ते लिहा. फरक नोंदला जाईल.',
    'stock.useHint': 'फुटणे, मुदत संपणे किंवा खराब नमुन्यासाठी. लॅब तपासण्या स्वतःचा वापर नोंदवतात.',
    'stock.recorded': 'नोंद झाली.',
    'stock.countSame': 'मोजणी जुळते, काही बदलायचे नाही.',
    'stock.countDiff': '{n} ने दुरुस्त केले.',
    'stock.kind.in': 'स्टॉक आला',
    'stock.kind.use': 'वापर',
    'stock.kind.adjust': 'मोजणी',
    'stock.by': '{name} यांनी',
    'stock.forTest': '{test} साठी',
    'stock.retire': 'वस्तू बंद करा',
    'stock.restore': 'वस्तू पुन्हा सुरू करा',
    'stock.retiredNote': 'बंद: कोणत्याही यादीत दिसत नाही, इतिहास जपला जातो.',
    'stock.viewOnly': 'तुम्ही स्टॉक पाहू शकता. बदलण्यासाठी ॲडमिनची परवानगी घ्या.',
    'stock.reports': 'स्टॉक अहवाल',
    'stock.rpStock': 'स्टॉक',
    'stock.rpUsage': 'वापर',
    'stock.rpTests': 'तपासणीनुसार',
    'stock.totalValue': 'एकूण स्टॉक किंमत',
    'stock.byCategory': 'प्रकारानुसार',
    'stock.toReorder': 'पुन्हा मागवा',
    'stock.nothingLow': 'सध्या काहीही कमी नाही.',
    'stock.usedCost': 'वापर (खर्च)',
    'stock.testsDone': 'पूर्ण तपासण्या',
    'stock.testIncome': 'तपासणीचे उत्पन्न',
    'stock.usedByItem': 'वस्तूनुसार वापर',
    'stock.noUsage': 'या कालावधीत स्टॉक वापरला गेला नाही.',
    'stock.received': 'आला {n}',
    'stock.used': 'वापर {n}',
    'stock.inTests': 'तपासण्यांत {n}',
    'stock.doneOfBooked': '{booked} पैकी {done} पूर्ण',
    'stock.stockCost': 'स्टॉक {cost}',
    'stock.margin': 'उरले {margin}',
    'stock.noTests': 'या कालावधीत लॅब तपासण्या नाहीत.',
    'stock.rpNote': 'वापराची किंमत त्या दिवशीच्या खरेदी किमतीने मोजली जाते.',
    'stock.itemsUsed': 'वापरलेल्या वस्तू',
    'stock.recipeTitle': '{name} साठी काय लागते',
    'stock.recipeNote': 'ही तपासणी पूर्ण केल्यावर हे आपोआप भरले जाते, लॅबला फक्त खात्री करावी लागते.',
    'stock.recipeNone': 'या तपासणीसाठी वस्तू ठरवलेल्या नाहीत.',
    'stock.recipeAdd': 'वस्तू जोडा',
    'stock.recipePick': 'वस्तू निवडा',
    'stock.recipeSave': 'जतन करा',
    'stock.recipeSaved': 'या तपासणीसाठी जतन केले.',
    'stock.usesN': '{n} वस्तू लागतात',
    'stock.noRecipe': 'वस्तू ठरवलेल्या नाहीत',
    'stock.markDone': 'पूर्ण करा',
    'stock.doneTitle': '{test} पूर्ण करा',
    'stock.doneNote': 'काय वापरले ते खात्री करा. ते आता स्टॉकमधून कमी होईल.',
    'stock.doneNoRecipe': 'या तपासणीसाठी वस्तू ठरवलेल्या नाहीत. जे वापरले ते जोडा, किंवा स्टॉकशिवाय पूर्ण करा.',
    'stock.addOther': 'आणखी वस्तू जोडा',
    'stock.confirmDone': 'पूर्ण करा',
    'stock.doneToast': '{test} पूर्ण झाली.',
    'stock.usedInTest': 'या तपासणीत वापर',
    'stock.short': 'फक्त {n} {unit} उपलब्ध',
    'stock.remove': 'काढा',
    'stock.canUpdate': 'स्टॉक बदलू शकतात',
    'stock.canUpdateHint': 'ही व्यक्ती स्टॉक जोडू शकेल, मोजू शकेल आणि प्रत्येक तपासणीचे साहित्य ठरवू शकेल.',
    'stock.switchedOn': '{name} आता स्टॉक बदलू शकतात.',
    'stock.switchedOff': '{name} आता स्टॉक बदलू शकत नाहीत.',
    'stock.labToday': 'आजची लॅब',
    'stock.labToDo': 'करायचे',
    'stock.labDone': 'पूर्ण',
    'stock.runningLow': 'कमी पडत आहे',
    'stock.openStock': 'स्टॉक उघडा',
    'stock.labTests': 'लॅब तपासण्या',
    'stock.allGood': 'प्रत्येक वस्तू अलर्ट पातळीच्या वर आहे.',
  },
  or: {
    'stock.outOfStock': 'ଷ୍ଟକ୍ ଶେଷ',
    'stock.lowAt': 'କମ୍ (≤ {n})',
    'stock.allow': 'ଷ୍ଟକ୍ ବଦଳାଇବାକୁ ଦିଅନ୍ତୁ',
    'stock.revoke': 'ଷ୍ଟକ୍ ବଦଳାଇବା ବନ୍ଦ କରନ୍ତୁ',
    'stock.price': 'ପ୍ରତି ଏକକ ଦାମ୍',
    'stock.title': 'ଷ୍ଟକ୍',
    'stock.items': 'ଆଇଟମ୍',
    'stock.value': 'ଷ୍ଟକ୍ ମୂଲ୍ୟ',
    'stock.low': 'କମ୍',
    'stock.out': 'ଶେଷ',
    'stock.search': 'ଆଇଟମ୍ ଖୋଜନ୍ତୁ',
    'stock.onHand': 'ଉପଲବ୍ଧ',
    'stock.perUnit': 'ପ୍ରତି {unit} {price}',
    'stock.worth': 'ମୂଲ୍ୟ {value}',
    'stock.alertAt': '{n} ରେ ଆଲର୍ଟ',
    'stock.noItems': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଷ୍ଟକ୍ ଆଇଟମ୍ ନାହିଁ',
    'stock.noItemsBody': 'ଆଇଟମ୍ ଯୋଡ଼ନ୍ତୁ, କିମ୍ବା ଦାମ୍ ସହ ଆପଣଙ୍କ ତାଲିକା ଇମ୍ପୋର୍ଟ କରନ୍ତୁ। ପରିମାଣ ପରେ ଗଣି ଭରିହେବ।',
    'stock.noMatch': 'କିଛି ମିଳିଲା ନାହିଁ',
    'stock.noMatchBody': 'ଅନ୍ୟ ଶବ୍ଦ ଚେଷ୍ଟା କରନ୍ତୁ, କିମ୍ବା ଫିଲ୍ଟର୍ ହଟାନ୍ତୁ।',
    'stock.addItem': 'ଆଇଟମ୍ ଯୋଡ଼ନ୍ତୁ',
    'stock.importList': 'ତାଲିକା ଇମ୍ପୋର୍ଟ',
    'stock.newItem': 'ନୂଆ ଷ୍ଟକ୍ ଆଇଟମ୍',
    'stock.editItem': 'ଆଇଟମ୍ ବଦଳାନ୍ତୁ',
    'stock.name': 'ଆଇଟମ୍ ନାମ',
    'stock.unit': 'ଏକକ',
    'stock.unitPh': 'ପିସ୍, ମିଲି, ବାକ୍ସ…',
    'stock.category': 'ବର୍ଗ',
    'stock.unitCost': 'ପ୍ରତି ଏକକ କିଣା ଦାମ୍ (₹)',
    'stock.reorderLevel': 'ଷ୍ଟକ୍ ଏତିକି ହେଲେ ଆଲର୍ଟ',
    'stock.reorderHint': 'ଆଲର୍ଟ ଦରକାର ନଥିଲେ 0 ରଖନ୍ତୁ।',
    'stock.opening': 'ବର୍ତ୍ତମାନ ଉପଲବ୍ଧ ଷ୍ଟକ୍',
    'stock.openingHint': 'ଇଚ୍ଛାଧୀନ। ପରେ ଗଣି ଭରିପାରିବେ।',
    'stock.saveItem': 'ଆଇଟମ୍ ସେଭ୍ କରନ୍ତୁ',
    'stock.itemAdded': '{name} ଯୋଡ଼ାଗଲା।',
    'stock.itemSaved': 'ସେଭ୍ ହେଲା।',
    'stock.couldNotLoad': 'ଷ୍ଟକ୍ ଲୋଡ୍ ହେଲା ନାହିଁ।',
    'stock.couldNotSave': 'ସେଭ୍ ହେଲା ନାହିଁ।',
    'stock.importTitle': 'ଆପଣଙ୍କ ଆଇଟମ୍ ତାଲିକା ଇମ୍ପୋର୍ଟ',
    'stock.importNote': 'ପ୍ରତି ଧାଡ଼ିରେ ଗୋଟିଏ ଆଇଟମ୍: ନାମ, ଏକକ, ଦାମ୍, ଆଲର୍ଟ ସ୍ତର, ପରିମାଣ। ପରିମାଣ ଇଚ୍ଛାଧୀନ। ତାଲିକାରେ ଆଗରୁ ଥିବା ଆଇଟମ୍‌ର କେବଳ ଦାମ୍ ଓ ଏକକ ବଦଳେ, ପରିମାଣ କେବେ ନୁହେଁ।',
    'stock.importPh': 'EDTA ଟ୍ୟୁବ୍, ପିସ୍, 6, 20\nଦସ୍ତାନା, ଯୋଡ଼ା, 4, 10, 100',
    'stock.pickFile': 'CSV ଫାଇଲ୍ ବାଛନ୍ତୁ',
    'stock.importGo': '{n} ଆଇଟମ୍ ଇମ୍ପୋର୍ଟ କରନ୍ତୁ',
    'stock.importDone': '{created} ଯୋଡ଼ାଗଲା, {updated} ବଦଳିଲା।',
    'stock.importNone': 'ସେହି ଲେଖାରେ କୌଣସି ଆଇଟମ୍ ମିଳିଲା ନାହିଁ।',
    'stock.history': 'ଇତିହାସ',
    'stock.noHistory': 'ଏପର୍ଯ୍ୟନ୍ତ କିଛି ରେକର୍ଡ ନାହିଁ।',
    'stock.usedIn': 'ଏହି ପରୀକ୍ଷାରେ ବ୍ୟବହୃତ',
    'stock.stockIn': 'ଷ୍ଟକ୍ ଆସିଲା',
    'stock.count': 'ଗଣନା',
    'stock.use': 'ବ୍ୟବହାର',
    'stock.quantity': 'ପରିମାଣ',
    'stock.supplier': 'ଯୋଗାଣକାରୀ',
    'stock.note': 'ଟିପ୍ପଣୀ',
    'stock.noteBill': 'ବିଲ୍ ନମ୍ବର କିମ୍ବା ମନ୍ତବ୍ୟ',
    'stock.newPrice': 'ପ୍ରତି ଏକକ କିଣା ଦାମ୍ (₹)',
    'stock.newPriceHint': 'ଏହା ଆଇଟମ୍‌ର ବର୍ତ୍ତମାନ ଦାମ୍ ବଦଳାଏ।',
    'stock.counted': 'ସେଲ୍ଫରେ ଗଣିଥିବା',
    'stock.countHint': 'ପ୍ରକୃତରେ ଯାହା ଅଛି ଲେଖନ୍ତୁ। ପାର୍ଥକ୍ୟ ରେକର୍ଡ ହେବ।',
    'stock.useHint': 'ଭଙ୍ଗା, ମିଆଦ ଶେଷ କିମ୍ବା ଖରାପ ନମୁନା ପାଇଁ। ଲ୍ୟାବ୍ ପରୀକ୍ଷା ନିଜ ବ୍ୟବହାର ନିଜେ ରେକର୍ଡ କରେ।',
    'stock.recorded': 'ରେକର୍ଡ ହେଲା।',
    'stock.countSame': 'ଗଣନା ମେଳ ଖାଉଛି, କିଛି ବଦଳାଇବାକୁ ନାହିଁ।',
    'stock.countDiff': '{n} ଦ୍ୱାରା ସଂଶୋଧିତ।',
    'stock.kind.in': 'ଷ୍ଟକ୍ ଆସିଲା',
    'stock.kind.use': 'ବ୍ୟବହାର',
    'stock.kind.adjust': 'ଗଣନା',
    'stock.by': '{name} ଙ୍କ ଦ୍ୱାରା',
    'stock.forTest': '{test} ପାଇଁ',
    'stock.retire': 'ଆଇଟମ୍ ବନ୍ଦ',
    'stock.restore': 'ଆଇଟମ୍ ପୁଣି ଚାଲୁ',
    'stock.retiredNote': 'ବନ୍ଦ: କୌଣସି ତାଲିକାରେ ଦେଖାଯାଏ ନାହିଁ, ଇତିହାସ ରହେ।',
    'stock.viewOnly': 'ଆପଣ ଷ୍ଟକ୍ ଦେଖିପାରିବେ। ବଦଳାଇବାକୁ ଆଡମିନ୍‌ଙ୍କ ଅନୁମତି ନିଅନ୍ତୁ।',
    'stock.reports': 'ଷ୍ଟକ୍ ରିପୋର୍ଟ',
    'stock.rpStock': 'ଷ୍ଟକ୍',
    'stock.rpUsage': 'ବ୍ୟବହାର',
    'stock.rpTests': 'ପରୀକ୍ଷା ଅନୁସାରେ',
    'stock.totalValue': 'ମୋଟ ଷ୍ଟକ୍ ମୂଲ୍ୟ',
    'stock.byCategory': 'ବର୍ଗ ଅନୁସାରେ',
    'stock.toReorder': 'ପୁଣି ମଗାନ୍ତୁ',
    'stock.nothingLow': 'ବର୍ତ୍ତମାନ କିଛି କମ୍ ନାହିଁ।',
    'stock.usedCost': 'ବ୍ୟବହାର (ଖର୍ଚ୍ଚ)',
    'stock.testsDone': 'ସରିଥିବା ପରୀକ୍ଷା',
    'stock.testIncome': 'ପରୀକ୍ଷାରୁ ଆୟ',
    'stock.usedByItem': 'ଆଇଟମ୍ ଅନୁସାରେ ବ୍ୟବହାର',
    'stock.noUsage': 'ଏହି ସମୟରେ କୌଣସି ଷ୍ଟକ୍ ବ୍ୟବହାର ହୋଇନାହିଁ।',
    'stock.received': 'ଆସିଲା {n}',
    'stock.used': 'ବ୍ୟବହାର {n}',
    'stock.inTests': 'ପରୀକ୍ଷାରେ {n}',
    'stock.doneOfBooked': '{booked} ମଧ୍ୟରୁ {done} ସରିଲା',
    'stock.stockCost': 'ଷ୍ଟକ୍ {cost}',
    'stock.margin': 'ବଳିଲା {margin}',
    'stock.noTests': 'ଏହି ସମୟରେ କୌଣସି ଲ୍ୟାବ୍ ପରୀକ୍ଷା ନାହିଁ।',
    'stock.rpNote': 'ବ୍ୟବହାରର ମୂଲ୍ୟ ସେଦିନର କିଣା ଦାମ୍ ଅନୁସାରେ ଗଣାଯାଏ।',
    'stock.itemsUsed': 'ବ୍ୟବହୃତ ଆଇଟମ୍',
    'stock.recipeTitle': '{name} ପାଇଁ କଣ ଲାଗେ',
    'stock.recipeNote': 'ଏହି ପରୀକ୍ଷା ସରିଲେ ଏଗୁଡ଼ିକ ନିଜେ ଭରିଯାଏ, ଲ୍ୟାବ୍ କେବଳ ନିଶ୍ଚିତ କରେ।',
    'stock.recipeNone': 'ଏହି ପରୀକ୍ଷା ପାଇଁ କୌଣସି ଆଇଟମ୍ ଠିକ୍ ହୋଇନାହିଁ।',
    'stock.recipeAdd': 'ଆଇଟମ୍ ଯୋଡ଼ନ୍ତୁ',
    'stock.recipePick': 'ଆଇଟମ୍ ବାଛନ୍ତୁ',
    'stock.recipeSave': 'ସେଭ୍ କରନ୍ତୁ',
    'stock.recipeSaved': 'ଏହି ପରୀକ୍ଷା ପାଇଁ ସେଭ୍ ହେଲା।',
    'stock.usesN': '{n} ଆଇଟମ୍ ଲାଗେ',
    'stock.noRecipe': 'କୌଣସି ଆଇଟମ୍ ଠିକ୍ ନାହିଁ',
    'stock.markDone': 'ସରିଲା ବୋଲି ଚିହ୍ନାନ୍ତୁ',
    'stock.doneTitle': '{test} ସରିଲା',
    'stock.doneNote': 'କଣ ବ୍ୟବହାର ହେଲା ନିଶ୍ଚିତ କରନ୍ତୁ। ଏହା ଏବେ ଷ୍ଟକ୍‌ରୁ କମିବ।',
    'stock.doneNoRecipe': 'ଏହି ପରୀକ୍ଷା ପାଇଁ କୌଣସି ଆଇଟମ୍ ଠିକ୍ ନାହିଁ। ଯାହା ଲାଗିଲା ଯୋଡ଼ନ୍ତୁ, କିମ୍ବା ଷ୍ଟକ୍ ବିନା ସାରନ୍ତୁ।',
    'stock.addOther': 'ଆଉ ଆଇଟମ୍ ଯୋଡ଼ନ୍ତୁ',
    'stock.confirmDone': 'ସରିଲା',
    'stock.doneToast': '{test} ସରିଲା।',
    'stock.usedInTest': 'ଏହି ପରୀକ୍ଷାରେ ବ୍ୟବହାର',
    'stock.short': 'କେବଳ {n} {unit} ଅଛି',
    'stock.remove': 'ହଟାନ୍ତୁ',
    'stock.canUpdate': 'ଷ୍ଟକ୍ ବଦଳାଇପାରିବେ',
    'stock.canUpdateHint': 'ଏହି ବ୍ୟକ୍ତି ଷ୍ଟକ୍ ଯୋଡ଼ିପାରିବେ, ଗଣିପାରିବେ ଓ ପ୍ରତି ପରୀକ୍ଷାର ସାମଗ୍ରୀ ଠିକ୍ କରିପାରିବେ।',
    'stock.switchedOn': '{name} ଏବେ ଷ୍ଟକ୍ ବଦଳାଇପାରିବେ।',
    'stock.switchedOff': '{name} ଆଉ ଷ୍ଟକ୍ ବଦଳାଇପାରିବେ ନାହିଁ।',
    'stock.labToday': 'ଆଜିର ଲ୍ୟାବ୍',
    'stock.labToDo': 'କରିବାକୁ ବାକି',
    'stock.labDone': 'ସରିଛି',
    'stock.runningLow': 'କମୁଛି',
    'stock.openStock': 'ଷ୍ଟକ୍ ଖୋଲନ୍ତୁ',
    'stock.labTests': 'ଲ୍ୟାବ୍ ପରୀକ୍ଷା',
    'stock.allGood': 'ପ୍ରତ୍ୟେକ ଆଇଟମ୍ ଆଲର୍ଟ ସ୍ତରରୁ ଉପରେ ଅଛି।',
  },
})
